



















import { computed, defineComponent, onMounted, reactive, ref, toRef, watchEffect } from '@vue/composition-api';
import { Feature, Point, Polygon } from 'geojson';
import { Layer } from 'mapbox-gl';
import { createLayers, markerClickHandler, polygonClickHandler } from '@/components/map/mapUtils';
import MglMap from '@/components/map/MglMap.vue';
import { ControlTypes } from '@/components/map/mglUtils';
import { FIELDS_MAP_ID } from '@/constants';
import { Area, FieldRow } from '@/store/types';
import { getMapOptionsForFieldsView } from '@/utils/mapFetching';
import FieldRowPopup from './FieldRowPopup.vue';

export default defineComponent({
	components: { MglMap, FieldRowPopup },
	props: {
		mobile: {
			type: Boolean,
		},
	},
	setup(props, { root, emit }) {
		const mobileRef = toRef(props, 'mobile');
		const mapRef = ref<HTMLElement | null>(null);
		const mapSize = reactive<{ width: number; height: number }>({ width: 0, height: 0 });
		const areas = root.$store.getters['resources/getAreas'];
		const fieldRows = computed<FieldRow[]>(() => root.$store.getters['fieldsView/getFieldRows']);
		const activeFieldId = computed<string>(() => root.$store.getters['fieldsView/getActiveFieldId']);

		const mapOptions = computed(() =>
			!mobileRef.value
				? getMapOptionsForFieldsView(areas, window.innerWidth / 2, Math.floor(window.innerHeight * 0.9))
				: mapSize.width > 0 && mapSize.height >= 72
				? getMapOptionsForFieldsView(areas, mapSize.width, mapSize.height / 2 - 36, 0, -mapSize.height / 4)
				: null
		);

		const layers = computed<Layer[]>(() => {
			const getAreasByFieldId = root.$store.getters['resources/getAreasByFieldId'];
			const areas: Feature<Polygon>[] = [];
			const markers: Feature<Point>[] = [];
			fieldRows.value.forEach((fieldRow) => {
				const fieldAreas: Area[] = getAreasByFieldId(fieldRow.id);
				areas.push(
					...fieldAreas.map((area) => ({
						...area,
						properties: { ...area.properties, id: fieldRow.id },
					}))
				);
				if (fieldRow.lngLat) {
					markers.push({
						id: fieldRow.id,
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: fieldRow.lngLat,
						},
						properties: { id: fieldRow.id },
					});
				}
			});

			return createLayers({
				areas,
				activeAreas: areas.filter((feature) => feature.properties?.id === activeFieldId.value),
				markers: markers,
				activeMarkers: markers.filter((marker) => marker.id === activeFieldId.value),
				polygon: true,
			});
		});

		const handleMouseDown = () => {
			emit('focus');
		};

		const setActiveFieldId = (fieldId: string) => {
			root.$store.dispatch('fieldsView/setActiveFieldId', fieldId);
		};

		const setMapSize = () => {
			mapSize.width = mapRef.value?.clientWidth ?? 0;
			mapSize.height = mapRef.value?.clientHeight ?? 0;
		};

		onMounted(() => {
			setMapSize();
		});

		watchEffect((cleanup) => {
			window.addEventListener('resize', setMapSize);

			cleanup(() => {
				window.removeEventListener('resize', setMapSize);
			});
		});

		return {
			mapRef,
			layers,
			controls: [ControlTypes.Geolocate],
			eventHandlers: [polygonClickHandler, markerClickHandler],
			mapOptions,
			setActiveFieldId,
			handleMouseDown,
			FIELDS_MAP_ID,
		};
	},
});
